import { Injectable, forwardRef, Inject } from '@angular/core';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import {
  AccountInfo,
  AuthenticationResult,
  InteractionRequiredAuthError,
  InteractionStatus,
} from '@azure/msal-browser';
import { Observable, Subject, from, of, throwError } from 'rxjs';
import { catchError, filter, map, take, tap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AzureAuthService {
  private readonly authInitialized = new Subject<void>();
  private readonly tokenSubject = new Subject<string>();
  private token: string | null = null;
  private readonly scopes = environment.azure.auth.scopes;

  constructor(
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
  ) {}

  public initializeApp(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.msalService.initialize().subscribe({
        next: () => {
          this.authInitialized.next();
          this.initializeAuth();
          this.acquireTokenSilently().subscribe({
            next: (token) => {
              //  console.log('Token acquired during app initialization:', token);
              this.tokenSubject.next(token);
              resolve();
            },
            error: (error) => {
              console.warn('No token acquired during initialization:', error);
              resolve(); // Resolve even if token acquisition fails to avoid blocking app startup
            },
          });
        },
        error: (error) => {
          console.error('Error initializing MSAL:', error);
          reject(error);
        },
      });
    });
  }

  public getAuthInitializedSubject(): Observable<void> {
    return this.authInitialized.asObservable();
  }

  private initializeAuth(): void {
    this.msalService.instance
      .handleRedirectPromise()
      .then((res: AuthenticationResult | null) => {
        if (res?.account) {
          this.setActiveAccount(res.account);
        }
      })
      .catch((error) => {
        console.error('Error handling redirect promise:', error);
      });

    // Monitor interaction status
    this.msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status !== InteractionStatus.None,
        ),
        take(1),
      )
      .subscribe(() => {});

    this.msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None,
        ),
        take(1),
      )
      .subscribe(() => {
        this.checkAndSetActiveAccount();
      });

    this.acquireTokenSilently();
  }

  private checkAndSetActiveAccount(): void {
    const accounts = this.msalService.instance.getAllAccounts();
    if (accounts.length > 0) {
      this.msalService.instance.setActiveAccount(accounts[0]);
    }
  }

  private setActiveAccount(account: AccountInfo): void {
    this.msalService.instance.setActiveAccount(account);
    this.acquireTokenSilently().subscribe();
  }

  isLoggedIn(): boolean {
    return !!this.msalService.instance.getActiveAccount();
  }

  getUser(): AccountInfo | null {
    const account = this.msalService.instance.getActiveAccount();
    if (account) {
      return account;
    }
    return null;
  }

  login(): Observable<AuthenticationResult> {
    return from(this.msalService.loginPopup({ scopes: this.scopes })).pipe(
      tap((response: AuthenticationResult) => {
        if (response.account) {
          const accessToken = response.accessToken;
          this.tokenSubject.next(accessToken);
          this.setActiveAccount(response.account);
        }
      }),
      catchError((error) => {
        if (error.errorCode === 'interaction_in_progress') {
          console.warn(
            'Interaction in progress detected. Please complete or cancel the current authentication.',
          );
          return throwError(
            () =>
              new Error(
                'Please complete or cancel the current authentication process before trying again.',
              ),
          );
        }
        console.error('Login error:', error);
        return throwError(() => error);
      }),
    );
  }

  logout(): Observable<boolean> {
    return from(
      this.msalService.logoutRedirect({
        account: this.msalService.instance.getActiveAccount(),
        postLogoutRedirectUri: environment.azure.auth.logoutRedirect,
      }),
    ).pipe(
      map(() => {
        //   console.log('Logout successful');
        this.token = null;
        this.tokenSubject.next('');
        this.msalService.instance.setActiveAccount(null);
        this.msalService.instance.clearCache();
        return true;
      }),
      catchError((error) => {
        console.error('Logout error:', error);
        return of(false);
      }),
    );
  }

  acquireTokenSilently(): Observable<string> {
    const tokenRequest = {
      scopes: this.scopes,
      account: this.getUser() || undefined,
    };

    return from(this.msalService.acquireTokenSilent(tokenRequest)).pipe(
      map((result: AuthenticationResult) => {
        this.token = result.accessToken;
        this.tokenSubject.next(this.token);
        return this.token;
      }),
      catchError((error) => {
        if (error instanceof InteractionRequiredAuthError) {
          // Fallback to interactive login
          return from(this.msalService.acquireTokenPopup(tokenRequest)).pipe(
            map((result: AuthenticationResult) => {
              this.token = result.accessToken;
              this.tokenSubject.next(this.token);
              return this.token;
            }),
            catchError((popupError) => {
              if (popupError.errorCode === 'interaction_in_progress') {
                console.warn(
                  'Interaction in progress during token acquisition.',
                );
                return throwError(
                  () =>
                    new Error(
                      'Please complete or cancel the current authentication process before trying again.',
                    ),
                );
              }
              console.error('Error acquiring token via popup:', popupError);
              return throwError(() => popupError);
            }),
          );
        }
        console.error('Error acquiring token silently:', error);
        return throwError(() => error);
      }),
    );
  }

  getAccessToken(): Observable<string | null> {
    if (this.token) {
      return of(this.token);
    }
    return this.acquireTokenSilently().pipe(
      catchError((error) => {
        console.error('Error acquiring access token:', error);
        return of(null); // Return null if token acquisition fails
      }),
    );
  }

  getTokenSubject(): Observable<string> {
    return this.tokenSubject.asObservable();
  }

  cancelInteraction(): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      // Clear any pending redirect promises
      this.msalService.instance
        .handleRedirectPromise()
        .then(() => {
          // Clear active account to force re-authentication
          this.msalService.instance.setActiveAccount(null);
          this.token = null;
          this.tokenSubject.next('');
          observer.next(true);
          observer.complete();
        })
        .catch((error) => {
          console.error('Error clearing redirect promise:', error);
          observer.next(true);
          observer.complete();
        });
    });
  }

  decodeJWTPayload(token: string): JWTPayload | null {
    try {
      // Split the token into its parts (header, payload, signature)
      const [header, payload, signature] = token.split('.');

      // Decode the Base64Url-encoded payload
      const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = atob(base64);

      // Parse the JSON string to an object
      const decodedPayload: JWTPayload = JSON.parse(jsonPayload);

      return decodedPayload;
    } catch (error) {
      console.error('Error decoding JWT:', error);
      return null;
    }
  }
}

interface JWTPayload {
  aud: string;
  iss: string;
  iat: number;
  nbf: number;
  exp: number;
  acr: string;
  aio: string;
  amr: string[];
  appid: string;
  appidacr: string;
  email: string;
  family_name: string;
  given_name: string;
  idp: string;
  ipaddr: string;
  name: string;
  oid: string;
  rh: string;
  scp: string;
  sid: string;
  sub: string;
  tid: string;
  unique_name: string;
  uti: string;
  ver: string;
  xms_ftd: string;
}
